import beaconReport from '@utils/beacon';
import type { PerformanceInfo } from '@tencent/wemeet-js-sdk';
import getInstanceId from '@utils/ua/get-instance-id';
import isServer from '@utils/env/is-server';
import { isWemeetApp } from './is-wemeet-app';

let isReportPerformance = false;

/**
  // getPerformance返回的数据：
  click_start_time: 1615446105484
  webview_init_time: 1615446105484     - click_to_webview_init_ms
  url_load_time: 1615446105542         - click_to_url_load_ms
  webview_start_time: 1615446105484    - click_to_webview_start_ms
  page_start_time: 1615446105689       - click_to_page_start_ms
  page_finished_time: 1615446111990    - click_to_page_finished_ms
  is_reload_url: false
  is_use_local_src: false
  system_version: "14.0.1"
  wemeet_version: "2.8.4.408"
  model_version: "iPhone12,5"
  network_type: "wifi"
  get_webview_time: 0
  is_preload_web_process: false
  is_webview_cache: false
  package_check_finish_time: 0
  redirect302_time: 0

  // 时序
  click_start_time - 点击时间
  webview_init_time - 初始化时间，预加载的时候，可能在click之前
  url_load_time - url开始加载时间（终端调用webview接口传入url开始加载）
  webview_start_time - webview开始加载时间（由webview回调）
  page_start_time - 页面开始渲染时间（由webview回调）
  page_finished_time - 页面渲染完成可交互时间（由webview回调）

 */
/**
 * 原始数据上报灯塔
 * @param data
 */
const reportBeacon = (data: PerformanceInfo) => {
  const { name } = getInstanceId(navigator.userAgent);
  const performanceTiming = reportPerformanceTiming();
  // webview数据，全量上报灯塔
  beaconReport('webview_web_page_performance', {
    pageurl: window.location.pathname,
    plat: name,
    click_to_webview_init_ms: calcDateTime(data.webviewInitTime, data.urlLoadTime),
    click_to_url_load_ms: calcDateTime(data.urlLoadTime, data.urlLoadTime),
    click_to_webview_start_ms: calcDateTime(data.webviewStartTime, data.urlLoadTime),
    click_to_page_start_ms: calcDateTime(data.pageStartTime, data.urlLoadTime),
    click_to_page_finished_ms: calcDateTime(data.pageFinishedTime, data.urlLoadTime),
    page_render_ms: calcDateTime(data.pageFinishedTime, data.pageStartTime),
    ...decodeCamelCase(data),
    ...performanceTiming,
    click_to_render_ms: calcDateTime(performanceTiming.domInteractive, data.urlLoadTime),
    sw_state: navigator?.serviceWorker?.controller?.state,
    sw_url: navigator?.serviceWorker?.controller?.scriptURL,
  });
};

/**
 * 页面指标上报TAM
 * @param data
 */
const reportAgies = (data: PerformanceInfo) => {
  const { pathname } = window.location;

  const webviewStart = calcDateTime(data.webviewStartTime, data.clickStartTime);
  const webviewCreate = calcDateTime(data.pageStartTime, data.webviewStartTime);
  const webviewPageLoad = calcDateTime(data.pageFinishedTime, data.pageStartTime);
  window.nextAegis.reportTime(`${pathname}_wvStart`, webviewStart);
  window.nextAegis.reportTime(`${pathname}_wvCreate`, webviewCreate);
  window.nextAegis.reportTime(`${pathname}_wvPageLoad`, webviewPageLoad);

  window.nextAegis.infoAll(
    `${pathname}_wvStart: ${webviewStart}, ${pathname}_wvCreate: ${webviewCreate}, ${pathname}_wvPageLoad: ${webviewPageLoad}`,
    {
      ...decodeCamelCase(data),
      sw_state: navigator?.serviceWorker?.controller?.state,
      sw_url: navigator?.serviceWorker?.controller?.scriptURL,
    },
  );
};

const calcDateTime = (start: string | number, to: string) => {
  const duration = Number(start) - Number(to);
  // 负数、大于10s数据抛弃
  if (duration < 0 || duration > 10 * 1000) {
    return 0;
  }
  return Math.floor(duration);
};

const reportPerformance = async ({
  /** 页面入口 */
  fromEntry = '',
  pageCacheTime = 0,
}) => {
  if (isReportPerformance) {
    return;
  }
  if (isServer) {
    return;
  }
  isReportPerformance = true;

  setTimeout(() => {
    window.nextAegis.infoAll('performanceTiming', reportPerformanceTiming());
    // webview测速上报
    if (isWemeetApp()) {
      wemeet.data
        .getPerformance()
        .then((data) => {
          const performanceData = {
            ...(data || {}),
            from_entry: fromEntry,
            page_cache_time: pageCacheTime,
          };
          console.log('webview performance', performanceData);
          reportBeacon(performanceData);
          reportAgies(performanceData);
        })
        .catch((e) => {
          console.log(`Error:getPerformance,${e}`);
        });
    }
    // 预加载可能 webView 关掉，尽快调用吧。
  }, 3000);
};

function reportPerformanceTiming() {
  const t: PerformanceTiming = performance.timing;
  // @todo 这里不知道为什么有时候 t.loadEventStart - t.domInteractive 返回一个很大的负数，暂时先简单处理
  let resourceDownload = t.loadEventStart - t.domInteractive;
  if (resourceDownload < 0) resourceDownload = 1070;
  const result = {
    dnsLookup: t.domainLookupEnd - t.domainLookupStart,
    tcp: t.connectEnd - t.connectStart,
    ssl: t.secureConnectionStart === 0 ? 0 : t.requestStart - t.secureConnectionStart,
    ttfb: t.responseStart - t.requestStart,
    contentDownload: t.responseEnd - t.responseStart,
    domParse: t.domInteractive - t.domLoading,
    resourceDownload,
    navigationStart: t.navigationStart,
    redirectStart: t.redirectStart,
    redirectEnd: t.redirectEnd,
    fetchStart: t.fetchStart,
    domainLookupStart: t.domainLookupStart,
    domainLookupEnd: t.domainLookupEnd,
    connectStart: t.connectStart,
    connectEnd: t.connectEnd,
    secureConnectionStart: t.secureConnectionStart,
    requestStart: t.requestStart,
    responseStart: t.responseStart,
    responseEnd: t.responseEnd,
    domLoading: t.domLoading,
    domInteractive: t.domInteractive,
    domContentLoadedEventStart: t.domContentLoadedEventStart,
    domContentLoadedEventEnd: t.domContentLoadedEventEnd,
    domComplete: t.domComplete,
    loadEventStart: t.loadEventStart,
    loadEventEnd: t.loadEventEnd,
  };
  return result;
}
/**
 * 驼峰转下划线
 * 不能有专有大写名词，例如URL, URI, HTTP
 */
function decodeCamelCase(data: PerformanceInfo): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  const camelCase = data as unknown as Record<string, string | number>;
  const keys = Object.keys(camelCase);
  const len = keys.length;
  for (let i = 0; i < len; i++) {
    const key = keys[i];
    const underKey: string = key.replace(
      /([A-Z])/g,
      (_, a1: string): string => `_${a1.toLowerCase()}`,
    );
    result[underKey] = camelCase[key];
  }
  return result;
}

/**
 * webview性能上报
 */
export default reportPerformance;
